/**
 * Centralized scroll lock management.
 *
 * Multiple components can request scroll lock simultaneously.
 * Scroll is only unlocked when all locks are released.
 *
 * Uses `overflow: hidden` on the document element only. This keeps the
 * body in normal flow as part of the html scroll container, so
 * `position: sticky` elements continue to resolve correctly while the
 * lock is active. Setting `overflow: hidden` on the body as well would
 * turn the body into its own scrollport and break sticky resolution.
 *
 * iOS Safari scrolls the page behind a fixed overlay when an input gains
 * focus — `overflow: hidden` alone does not block this. To handle it, the
 * lock temporarily escalates to `position: fixed` on the body while a
 * form field is focused, then reverts on blur. Sticky elements may
 * displace during escalation. In practice this happens inside the active
 * modal because `inert-stack` limits focus while a claim is active, but
 * `claimInert(..., { skip })` can intentionally leave regions outside the
 * modal interactive during the lock.
 */

const locks = new Set<string>();

const FOCUSABLE_SELECTOR = "input, textarea, select, [contenteditable]";

const needsFixedBodyEscalation =
  typeof navigator !== "undefined" &&
  (/iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.userAgent.includes("Mac") && "ontouchend" in document));

let savedScrollY = 0;
let savedScrollbarWidth = 0;
let escalated = false;

/**
 * Request a scroll lock. Sets `data-scroll-locked` on the document element.
 */
export function lockScroll(id: string): void {
  if (locks.size === 0) {
    savedScrollY = window.scrollY;
    savedScrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    if (savedScrollbarWidth > 0) {
      document.documentElement.style.paddingInlineEnd = `${savedScrollbarWidth}px`;
    }
    if (needsFixedBodyEscalation) {
      document.addEventListener("focusin", handleFocusIn, true);
      document.addEventListener("focusout", handleFocusOut, true);
    }
  }
  locks.add(id);
  document.documentElement.dataset.scrollLocked = "";
}

/**
 * Release a scroll lock. Removes `data-scroll-locked` when all locks are released.
 */
export function unlockScroll(id: string): void {
  if (!locks.delete(id)) return;
  if (locks.size === 0) {
    if (needsFixedBodyEscalation) {
      deescalate();
      document.removeEventListener("focusin", handleFocusIn, true);
      document.removeEventListener("focusout", handleFocusOut, true);
    }
    delete document.documentElement.dataset.scrollLocked;
    document.documentElement.style.paddingInlineEnd = "";
  }
}

function handleFocusIn(event: FocusEvent): void {
  const target = event.target;
  if (target instanceof HTMLElement && target.matches(FOCUSABLE_SELECTOR)) {
    escalate();
  }
}

function handleFocusOut(): void {
  // Defer so the next focusin has a chance to set activeElement before we
  // decide whether to deescalate.
  queueMicrotask(checkActive);
}

function checkActive(): void {
  const active = document.activeElement;
  if (!(active instanceof HTMLElement) || !active.matches(FOCUSABLE_SELECTOR)) {
    deescalate();
  }
}

function escalate(): void {
  if (escalated) return;
  const body = document.body.style;
  body.position = "fixed";
  body.top = `-${savedScrollY}px`;
  body.left = "0";
  body.right = `${savedScrollbarWidth}px`;
  escalated = true;
}

function deescalate(): void {
  if (!escalated) return;
  const body = document.body.style;
  body.position = "";
  body.top = "";
  body.left = "";
  body.right = "";
  window.scrollTo(0, savedScrollY);
  escalated = false;
}
